import Image from "next/image";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { View } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { LivePhotoProps } from "./types";

const LivePhoto = ({
	style,
	source,
	pointerEvents,
	onLoadEnd,
	onImageLoadEnd,
	onVideoLoadEnd,
	onPreviewLoadEnd,
	loadHeavy = true,
	webOverrideVideo,
}: LivePhotoProps) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [imageIsReady, setImageIsReady] = useState(false);
	const [videoIsReady, setVideoIsReady] = useState(false);

	useEffect(() => {
		if (webOverrideVideo && videoRef.current) {
			webOverrideVideo(videoRef.current);
		}
	}, [webOverrideVideo]);

	const handleMouseUp = useCallback(() => {
		videoRef.current?.pause();
		setIsPlaying(false);
	}, []);
	const gesture = useMemo(
		() =>
			Gesture.LongPress()
				.minDuration(200)
				.onStart(() => {
					if (videoRef.current) {
						videoRef.current.currentTime = 0;
						videoRef.current.play();
					}
					setIsPlaying(true);
				}),
		[],
	);

	const didImageLoad = useRef(false);
	const didVideoLoad = useRef(false);
	const handleMaybeLoadEnd = useCallback(() => {
		if (didImageLoad.current && didVideoLoad.current) {
			onLoadEnd?.();
		}
	}, [onLoadEnd]);
	const handleImageLoadEnd = useCallback(() => {
		if (didImageLoad.current) return;
		setImageIsReady(true);
		onPreviewLoadEnd?.({
			success: true,
		});
		onImageLoadEnd?.();
		didImageLoad.current = true;
		handleMaybeLoadEnd();
	}, [handleMaybeLoadEnd, onImageLoadEnd, onPreviewLoadEnd]);
	const handleVideoLoadEnd = useCallback(() => {
		if (didVideoLoad.current) return;
		setVideoIsReady(true);
		onVideoLoadEnd?.();
		didVideoLoad.current = true;
		handleMaybeLoadEnd();
	}, [onVideoLoadEnd, handleMaybeLoadEnd]);
	useEffect(() => {
		source.imageUri;

		didImageLoad.current = false;
	}, [source.imageUri]);
	useEffect(() => {
		source.videoUri;
		source.h264VideoUri;

		didVideoLoad.current = false;
	}, [source.videoUri, source.h264VideoUri]);
	const opacitySV = useSharedValue(videoIsReady && isPlaying ? 0 : 1);
	useEffect(() => {
		opacitySV.value = videoIsReady && isPlaying ? 0 : 1;
	}, [videoIsReady, isPlaying, opacitySV]);

	const animatedStyle = useAnimatedStyle(
		() => ({
			opacity: withTiming(opacitySV.value),
		}),
		[opacitySV],
	);

	return (
		<GestureDetector gesture={gesture}>
			<View
				style={style}
				pointerEvents={pointerEvents}
				// @ts-expect-error
				onMouseUp={handleMouseUp}
			>
				{loadHeavy && (
					<video
						onLoadedData={handleVideoLoadEnd}
						ref={videoRef}
						loop
						muted
						playsInline
						poster={source.imageUri}
						style={{
							position: "absolute",
							width: "100%",
							height: "100%",
							// Avoids weird top down flicker loading
							opacity: videoIsReady ? 1 : 0,
							transition: "opacity 0.2s ease-in-out",
						}}
					>
						<source src={source.h264VideoUri ?? source.videoUri} type="video/mp4" />
					</video>
				)}
				<Animated.View
					pointerEvents="none"
					style={[
						{
							position: "absolute",
							width: "100%",
							height: "100%",
						},
						animatedStyle,
					]}
				>
					<Image
						onLoad={handleImageLoadEnd}
						onError={() => {
							onPreviewLoadEnd?.({ success: false });
						}}
						alt="Preview of live photo"
						fill
						unoptimized
						objectFit="contain"
						style={{
							width: "100%",
							height: "100%",
							// Avoids weird top down flicker loading
							opacity: imageIsReady ? 1 : 0,
							transition: "opacity 0.2s ease-in-out",
						}}
						src={source.imageUri}
					/>
				</Animated.View>
			</View>
		</GestureDetector>
	);
};
export default LivePhoto;
